import {
  DynamoFile,
  FileCommentTopic,
  FolderItem,
  PopupTextType,
  TextcutRectangle,
} from '../../types'
import FilePathBreadcrum from '../FileUnorderedList/FilePathBreadcrum'
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual'
import { useRef } from 'react'
import MobileFileItem from './MobileFileItem'
import MobileFolderItem from './MobileFolderItem'
import { isFolderItem } from '../../utils/guards'

type Props = {
  files: DynamoFile[]
  onFileClick: (file: DynamoFile) => () => Promise<void>
  showPopup: (popup: PopupTextType) => void
  deletePDF: (fileId: string) => Promise<void>
  isTrimmable: (fileId: string) => any
  trimFile: (fileId: string) => void
  isExportable: (fileId: string) => boolean
  exportPDF: (file: DynamoFile) => () => Promise<void>
  downloadFile: (file: DynamoFile) => () => Promise<void>
  folders?: FolderItem[]
  folderStack?: FolderItem[]
  setFolderStack?: (stack: FolderItem[]) => void
  references: TextcutRectangle[]
  comments: FileCommentTopic[]
  deleteFolder?: (folderItem: FolderItem) => void
  movable?: boolean
  move?: ({
    id,
    name,
    type,
  }: {
    id: string
    name: string
    type: 'folder' | 'file'
  }) => void
  savePdfsToWorkbook: (file: DynamoFile) => Promise<void>
  showRenamePopup: (fileId: string) => void
  selectMultipleFiles: boolean
  addFileIdToSelectedFileRef: (fileId: string) => void
  fileHasChecked: (fileId: string) => boolean
  onFolderClick?: (
    folder: FolderItem,
    setFolderStack: ((stack: FolderItem[]) => void) | undefined
  ) => void
}

const MobileFileList = ({
  files,
  comments,
  deletePDF,
  downloadFile,
  exportPDF,
  isExportable,
  isTrimmable,
  onFileClick,
  savePdfsToWorkbook,
  showPopup,
  trimFile,
  deleteFolder,
  folderStack,
  folders,
  movable = false,
  move = () => {},
  setFolderStack,
  showRenamePopup,
  selectMultipleFiles,
  addFileIdToSelectedFileRef,
  fileHasChecked,
  onFolderClick,
}: Props) => {
  const folderArray = folders || []
  const items = [...folderArray, ...files].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  )

  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer: Virtualizer<HTMLDivElement, Element> = useVirtualizer({
    count: items.length,
    estimateSize: () => 100,
    getScrollElement: () => parentRef.current,
  })

  return (
    <ul>
      {folderStack && folderStack?.length !== 0 && setFolderStack && (
        <li className="relative rounded-md p-1 py-3">
          <FilePathBreadcrum
            folderStack={folderStack}
            setFolderStack={setFolderStack}
          />
        </li>
      )}
      <div ref={parentRef}>
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualItem) => {
            const item = items[virtualItem.index]

            if (isFolderItem(item)) {
              return (
                <MobileFolderItem
                  key={item.id}
                  folder={item}
                  virtualItem={virtualItem}
                  selectMultipleFiles={selectMultipleFiles}
                  addFileIdToSelectedFileRef={addFileIdToSelectedFileRef}
                  fileHasChecked={fileHasChecked}
                  showRenamePopup={showRenamePopup}
                  deleteFolder={deleteFolder}
                  movable={movable}
                  move={move}
                  onFolderClick={onFolderClick}
                  setFolderStack={setFolderStack}
                />
              )
            }

            return (
              <MobileFileItem
                key={item.fileId}
                file={item}
                virtualItem={virtualItem}
                addFileIdToSelectedFileRef={addFileIdToSelectedFileRef}
                comments={comments}
                deletePDF={deletePDF}
                downloadFile={downloadFile}
                exportPDF={exportPDF}
                fileHasChecked={fileHasChecked}
                isExportable={isExportable}
                isTrimmable={isTrimmable}
                movable={movable}
                onFileClick={onFileClick}
                savePdfsToWorkbook={savePdfsToWorkbook}
                selectMultipleFiles={selectMultipleFiles}
                showPopup={showPopup}
                showRenamePopup={showRenamePopup}
                trimFile={trimFile}
                move={move}
              />
            )
          })}
        </div>
      </div>
    </ul>
  )
}

export default MobileFileList
